import axios from 'axios';
import { useAuthStore } from '../stores/auth';
import { getApiUrl } from './config';
import router from '../router';
// Create axios instance
const axiosInstance = axios.create({
    baseURL: getApiUrl(),
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
    }
});
// Request interceptor
axiosInstance.interceptors.request.use((config) => {
    // Add auth token to headers
    const authStore = useAuthStore();
    const token = authStore.token;
    if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    // Add shop ID to headers if available
    const shopId = localStorage.getItem('selectedShopId');
    if (shopId && config.headers) {
        config.headers['X-Shop-Id'] = shopId;
    }
    // Log request in development
    if (process.env.NODE_ENV === 'development') {
        console.log('API Request:', {
            url: config.url,
            method: config.method,
            data: config.data,
            params: config.params
        });
    }
    return config;
}, (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
});
// Response interceptor
axiosInstance.interceptors.response.use((response) => {
    // Log response in development
    if (process.env.NODE_ENV === 'development') {
        console.log('API Response:', {
            url: response.config.url,
            status: response.status,
            data: response.data
        });
    }
    return response;
}, async (error) => {
    const authStore = useAuthStore();
    // Handle different error scenarios
    if (error.response) {
        const { status, data } = error.response;
        // Handle authentication errors
        if (status === 401) {
            // Token expired or invalid
            authStore.logout();
            router.push('/login');
            return Promise.reject(new Error('Authentication failed. Please login again.'));
        }
        // Handle forbidden errors
        if (status === 403) {
            return Promise.reject(new Error('You do not have permission to perform this action.'));
        }
        // Handle not found errors
        if (status === 404) {
            return Promise.reject(new Error('The requested resource was not found.'));
        }
        // Handle server errors
        if (status >= 500) {
            return Promise.reject(new Error('Server error. Please try again later.'));
        }
        // Handle validation errors and other client errors
        if (status === 422 || status === 400 || status === 409) {
            // Check for 'msg' field in the response
            const errorMessage = data?.msg || data?.message || 'Request failed';
            return Promise.reject(new Error(errorMessage));
        }
        // Handle any other error with msg field
        if (data?.msg) {
            return Promise.reject(new Error(data.msg));
        }
    }
    else if (error.request) {
        // Request was made but no response received
        return Promise.reject(new Error('Network error. Please check your connection.'));
    }
    // Log error in development
    if (process.env.NODE_ENV === 'development') {
        console.error('API Error:', error);
    }
    return Promise.reject(error);
});
// Export configured axios instance
export default axiosInstance;
// Convenience methods for common HTTP operations
export const api = {
    get: (url, config) => axiosInstance.get(url, config),
    post: (url, data) => axiosInstance.post(url, data),
    put: (url, data) => axiosInstance.put(url, data),
    patch: (url, data) => axiosInstance.patch(url, data),
    delete: (url) => axiosInstance.delete(url)
};
